import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ReportPageWrapper, { StatCard } from "./ReportPageWrapper";
import { Input } from "@/components/ui/input";
import {
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Landmark,
  DollarSign,
  Download,
  Printer,
  Search,
  RotateCcw,
} from "lucide-react";
import { format, subMonths } from "date-fns";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ComposedChart, Legend, PieChart, Pie, Cell,
} from "recharts";

const STATUS_COLORS: Record<string, string> = {
  Pending: "#EAB308", Approved: "#3B82F6", Rejected: "#EF4444",
  Active: "#009A44", Disbursed: "#8B5CF6", Completed: "#10B981",
  Defaulted: "#DC2626", Cancelled: "#6B7280",
};

function CustomTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium text-slate-700 mb-1">{label}</p>
        {payload.map((entry: any, idx: number) => (
          <p key={idx} style={{ color: entry.color }} className="text-xs">
            {entry.name}: {entry.value?.toLocaleString()} FRW
          </p>
        ))}
      </div>
    );
  }
  return null;
}

export default function LoanReport() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const { data: loansData, isLoading } = useQuery(
    ["my-loans-report", user?.id],
    async () => {
      const { data: res } = await api.get(`/teachers/${user?.id}/loans`);
      const d = res.data || res;
      return {
        loans: Array.isArray(d.loans) ? d.loans : [],
        totalBorrowed: d.totalBorrowed || 0,
        totalRepaid: d.totalRepaid || 0,
        outstandingBalance: d.outstandingBalance || 0,
      };
    },
    { enabled: !!user?.id }
  );

  const loans = loansData?.loans || [];

  const filtered = useMemo(() => {
    return loans.filter((l: any) => {
      const matchStatus = statusFilter === "all" || l.status === statusFilter;
      const matchSearch = !search ||
        (l.purpose || "").toLowerCase().includes(search.toLowerCase()) ||
        (l.status || "").toLowerCase().includes(search.toLowerCase());
      return matchStatus && matchSearch;
    });
  }, [loans, statusFilter, search]);

  const stats = useMemo(() => {
    const requested = loans.length;
    const approved = loans.filter((l: any) =>
      ["Approved", "Active", "Disbursed", "Completed"].includes(l.status)
    ).length;
    const pending = loans.filter((l: any) => l.status === "Pending").length;
    const rejected = loans.filter((l: any) => l.status === "Rejected").length;
    const disbursed = loans.filter((l: any) =>
      ["Disbursed", "Active"].includes(l.status)
    ).length;
    const outstanding = loansData?.outstandingBalance || loans
      .filter((l: any) => ["Active", "Approved"].includes(l.status))
      .reduce((s: number, l: any) => s + (l.dueAmount || l.amount || 0), 0);
    return { requested, approved, pending, rejected, disbursed, outstanding };
  }, [loans, loansData]);

  const trendData = useMemo(() => {
    const months: Record<string, { applied: number; approved: number; repaid: number }> = {};
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = subMonths(now, i);
      const key = format(d, "MMM yy");
      months[key] = { applied: 0, approved: 0, repaid: 0 };
    }
    loans.forEach((l: any) => {
      const appliedKey = format(new Date(l.applicationDate || l.createdAt), "MMM yy");
      if (months[appliedKey]) months[appliedKey].applied += l.amount || 0;
      if (l.approvalDate) {
        const approvedKey = format(new Date(l.approvalDate), "MMM yy");
        if (months[approvedKey]) months[approvedKey].approved += l.amount || 0;
      }
      if (l.payments) {
        l.payments.forEach((p: any) => {
          const key = format(new Date(p.paymentDate || p.createdAt), "MMM yy");
          if (months[key]) months[key].repaid += parseFloat(p.amount || p.principalAmount) || 0;
        });
      }
    });
    return Object.entries(months).map(([label, vals]) => ({ label, ...vals }));
  }, [loans]);

  const statusDist = useMemo(() => {
    const counts: Record<string, number> = {};
    loans.forEach((l: any) => {
      const s = l.status || "Unknown";
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [loans]);

  const formatCurrency = (val: number) => (val || 0).toLocaleString() + " FRW";

  const handleReset = () => { setSearch(""); setStatusFilter("all"); };

  const handleExport = () => {
    const headers = ["Application Date", "Amount", "Interest", "Term", "Purpose", "Status", "Approval Date", "Outstanding"];
    const rows = filtered.map((l: any) => [
      format(new Date(l.applicationDate || l.createdAt), "yyyy-MM-dd"),
      l.amount || 0, l.interestRate || 0, l.termMonths || "N/A",
      l.purpose || "N/A", l.status || "N/A",
      l.approvalDate ? format(new Date(l.approvalDate), "yyyy-MM-dd") : "N/A",
      l.dueAmount || 0,
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `loan-report-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl" />)}
        </div>
        <div className="h-64 bg-slate-100 rounded-xl" />
      </div>
    );
  }

  return (
    <ReportPageWrapper
      title="Loan Report"
      breadcrumbItems={[
        { label: "Dashboard", path: "/member/dashboard" },
        { label: "Member Reports", path: "/member/reports" },
        { label: "Loan Report" },
      ]}
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Loans Requested" value={stats.requested.toString()} icon={FileText} color="#3B82F6" />
        <StatCard label="Approved" value={stats.approved.toString()} icon={CheckCircle} color="#009A44" />
        <StatCard label="Pending" value={stats.pending.toString()} icon={Clock} color="#EAB308" />
        <StatCard label="Rejected" value={stats.rejected.toString()} icon={XCircle} color="#EF4444" />
        <StatCard label="Disbursed" value={stats.disbursed.toString()} icon={Landmark} color="#8B5CF6" />
        <StatCard label="Outstanding" value={formatCurrency(stats.outstanding)} icon={DollarSign} color="#EF4444" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Trend */}
        <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <BarChart className="h-4 w-4 text-[#009A44]" />
            Loan Trend
          </h3>
          {trendData.some(d => d.applied > 0 || d.approved > 0) ? (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="applied" name="Applied" fill="#EAB308" radius={[3, 3, 0, 0]} opacity={0.8} />
                  <Bar dataKey="approved" name="Approved" fill="#009A44" radius={[3, 3, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center py-8 bg-slate-50 rounded-lg">
              <BarChart className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No loan data yet</p>
            </div>
          )}
        </div>

        {/* Status Distribution */}
        <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <PieChart className="h-4 w-4 text-[#009A44]" />
            Loan Status Distribution
          </h3>
          {statusDist.length > 0 ? (
            <div className="h-52 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusDist} cx="50%" cy="50%"
                    innerRadius={45} outerRadius={75}
                    paddingAngle={3} dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {statusDist.map((entry, idx) => (
                      <Cell key={idx} fill={STATUS_COLORS[entry.name] || "#6B7280"} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center py-8 bg-slate-50 rounded-lg">
              <PieChart className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No loans to display</p>
            </div>
          )}
        </div>
      </div>

      {/* Loan Table Card */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search loans..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="h-9 px-3 text-xs border border-slate-200 rounded-lg bg-white text-slate-600"
            >
              <option value="all">All Status</option>
              {Object.keys(STATUS_COLORS).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <Button variant="ghost" size="sm" onClick={handleReset} className="text-slate-500 h-9">
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()} className="h-9">
              <Printer className="h-3.5 w-3.5 mr-1" /> Print
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} className="h-9">
              <Download className="h-3.5 w-3.5 mr-1" /> Export
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#009A44]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Application Date</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Amount</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Interest</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Term</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Purpose</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Approval</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.map((l: any, idx: number) => (
                <tr key={l.id || idx} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                    {format(new Date(l.applicationDate || l.createdAt), "MMM dd, yyyy")}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">
                    {(l.amount || 0).toLocaleString()} FRW
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600">{l.interestRate || 0}%</td>
                  <td className="px-4 py-3 text-center text-slate-600">{l.termMonths || "N/A"}m</td>
                  <td className="px-4 py-3 text-slate-600 max-w-[120px] truncate">{l.purpose || "N/A"}</td>
                  <td className="px-4 py-3">
                    <Badge
                      className="text-[10px]"
                      style={{
                        backgroundColor: (STATUS_COLORS[l.status] || "#6B7280") + "20",
                        color: STATUS_COLORS[l.status] || "#6B7280",
                        border: "none",
                      }}
                    >
                      {l.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {l.approvalDate ? format(new Date(l.approvalDate), "MMM dd") : "N/A"}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">
                    {(l.dueAmount || 0).toLocaleString()} FRW
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-500">
                    No loan records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </ReportPageWrapper>
  );
}
